/**
 * HistorySnapshotDialog — preview / diff / restore a snapshot.
 *
 * Tabs:
 *   • 内容 — raw snapshot content (read-only)
 *   • 差异 — unified diff vs current document
 *
 * Footer actions:
 *   • 删除 — delete snapshot
 *   • 恢复 — overwrite current document with snapshot content
 *           (with omit-fill choice + 30s undo toast)
 */

import { Clock, FileText, GitCompare, Trash2, Undo2, X } from "lucide-react";
import { useT } from "@magic-md/i18n/react";
import {
    type ReactNode,
    useCallback,
    useEffect,
    useMemo,
    useRef,
    useState,
} from "react";
import { createPortal } from "react-dom";

import { getPlatformService } from "../../platform";
import { useEditorStore } from "../../store/editor-store";
import { useHistoryStore, type SnapshotMeta } from "../../store/history-store";
import { cn } from "../../lib/utils";
import { DialogButton, DialogOverlay } from "./DialogOverlay";

/* ── Imperative API ── */

interface SnapshotDialogRequest {
    filePath: string;
    snapshotId: string;
    resolve: () => void;
}

let _listener: ((req: SnapshotDialogRequest) => void) | null = null;

export function showHistorySnapshotDialog(
    filePath: string,
    snapshotId: string,
): Promise<void> {
    return new Promise((resolve) => {
        if (_listener) {
            _listener({ filePath, snapshotId, resolve });
        } else {
            console.warn("[history-dialog] No HistorySnapshotDialogHost mounted");
            resolve();
        }
    });
}

/* ── Host ── */

export function HistorySnapshotDialogHost(): ReactNode {
    const [request, setRequest] = useState<SnapshotDialogRequest | null>(null);

    useEffect(() => {
        _listener = (req) => setRequest(req);
        return () => {
            _listener = null;
        };
    }, []);

    if (!request) return null;
    return (
        <HistorySnapshotDialog
            filePath={request.filePath}
            snapshotId={request.snapshotId}
            onClose={() => {
                request.resolve();
                setRequest(null);
            }}
        />
    );
}

/* ── Dialog ── */

interface DialogProps {
    filePath: string;
    snapshotId: string;
    onClose: () => void;
}

type Tab = "content" | "diff";

function HistorySnapshotDialog({ filePath, snapshotId, onClose }: DialogProps) {
    const t = useT("editor");
    const [tab, setTab] = useState<Tab>("content");
    const [snapshotContent, setSnapshotContent] = useState<string | null>(null);
    const [diffText, setDiffText] = useState<string | null>(null);
    const [meta, setMeta] = useState<SnapshotMeta | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [busy, setBusy] = useState(false);

    const refreshHistory = useHistoryStore((s) => s.refresh);
    const removeSnapshot = useHistoryStore((s) => s.remove);
    const restoreFromStore = useHistoryStore((s) => s.restore);
    const cachedSnapshots = useHistoryStore((s) => s.files[filePath]?.snapshots ?? []);

    // Load snapshot content + diff
    useEffect(() => {
        let cancelled = false;
        setLoading(true);
        setError(null);
        const platform = getPlatformService();
        const currentContent =
            useEditorStore.getState().tabs.find((tb) => tb.filePath === filePath)
                ?.content ?? "";

        Promise.all([
            platform.engine.historyGet({ filePath, snapshotId }),
            platform.engine.historyDiff({
                filePath,
                fromSnapshotId: snapshotId,
                currentContent,
            }),
        ])
            .then(([getRes, diffRes]) => {
                if (cancelled) return;
                setSnapshotContent(getRes.content);
                setMeta(getRes.snapshot);
                setDiffText(diffRes.unifiedDiff);
                setLoading(false);
            })
            .catch((err) => {
                if (cancelled) return;
                setError(err instanceof Error ? err.message : String(err));
                setLoading(false);
            });

        return () => {
            cancelled = true;
        };
    }, [filePath, snapshotId]);

    // Use cached meta as fallback for richer header info
    const effectiveMeta = useMemo(
        () => meta ?? cachedSnapshots.find((s) => s.id === snapshotId) ?? null,
        [meta, cachedSnapshots, snapshotId],
    );

    const handleRestore = useCallback(async () => {
        if (busy) return;
        setBusy(true);
        const platform = getPlatformService();
        const setStatusMessage = useEditorStore.getState().setStatusMessage;
        const currentTab = useEditorStore
            .getState()
            .tabs.find((t) => t.filePath === filePath);
        const currentContent = currentTab?.content ?? "";
        try {
            setStatusMessage(t("history.restore.inProgress"), undefined);
            const result = await restoreFromStore({
                filePath,
                snapshotId,
                currentContent,
            });
            if (!result) {
                setError(t("history.restore.failed"));
                setStatusMessage(t("history.restore.snapshotFailed"), 4000);
                setBusy(false);
                return;
            }

            // Persist to disk first, then update buffer via setExternalContent
            // (which marks not-dirty and records the save to suppress the
            // file-watcher echo). history-service dedupes by hash so the
            // on-save hook won't create a redundant snapshot.
            try {
                await platform.engine.workspaceWriteFile({
                    path: filePath,
                    content: result.content,
                });
            } catch (writeErr) {
                console.warn("[history-restore] disk write failed", writeErr);
            }
            const targetTab = useEditorStore
                .getState()
                .tabs.find((t) => t.filePath === filePath);
            if (targetTab) {
                useEditorStore
                    .getState()
                    .setExternalContent(result.content, targetTab.id);
            }

            // Show 30s undo toast if a pre-snapshot was captured.
            const undoTarget = result.preSnapshot;
            onClose();
            if (undoTarget) {
                showHistoryUndoToast({
                    message: t("history.restore.restoredTo", {
                        time: formatRelativeTime(effectiveMeta?.createdAt ?? Date.now(), t),
                    }),
                    onUndo: async () => {
                        const tabNow = useEditorStore
                            .getState()
                            .tabs.find((t) => t.filePath === filePath);
                        const undoResult = await restoreFromStore({
                            filePath,
                            snapshotId: undoTarget.id,
                            currentContent: tabNow?.content ?? "",
                        });
                        if (undoResult) {
                            await platform.engine
                                .workspaceWriteFile({
                                    path: filePath,
                                    content: undoResult.content,
                                })
                                .catch(() => {});
                            const t = useEditorStore
                                .getState()
                                .tabs.find((x) => x.filePath === filePath);
                            if (t) {
                                useEditorStore
                                    .getState()
                                    .setExternalContent(undoResult.content, t.id);
                            }
                        }
                    },
                });
            } else {
                setStatusMessage(t("history.restore.snapshotRestored"), 4000);
            }
            await refreshHistory(filePath).catch(() => {});
        } catch (err) {
            setError(err instanceof Error ? err.message : String(err));
            setStatusMessage(t("history.restore.snapshotFailedWithMessage", {
                message: err instanceof Error ? err.message : t("common.unknown"),
            }), 4000);
            setBusy(false);
        }
    }, [busy, filePath, snapshotId, restoreFromStore, refreshHistory, effectiveMeta, onClose, t]);

    const handleDelete = useCallback(async () => {
        if (busy) return;
        setBusy(true);
        const setStatusMessage = useEditorStore.getState().setStatusMessage;
        try {
            setStatusMessage(t("history.delete.inProgress"), undefined);
            const removed = await removeSnapshot(filePath, snapshotId);
            if (removed) {
                setStatusMessage(t("history.delete.deleted"), 4000);
                onClose();
            } else {
                setStatusMessage(t("history.delete.failed"), 4000);
                setBusy(false);
            }
        } catch (err) {
            setError(err instanceof Error ? err.message : String(err));
            setStatusMessage(t("history.delete.failedWithMessage", {
                message: err instanceof Error ? err.message : t("common.unknown"),
            }), 4000);
            setBusy(false);
        }
    }, [busy, filePath, snapshotId, removeSnapshot, onClose, t]);

    return (
        <DialogOverlay
            open
            onClose={onClose}
            title={
                effectiveMeta
                    ? t("history.snapshotWithTime", { time: formatTimestamp(effectiveMeta.createdAt) })
                    : t("history.snapshot")
            }
            className="w-[760px] max-w-[92vw]"
            footer={
                <>
                    <DialogButton onClick={handleDelete} disabled={busy}>
                        <Trash2 size={12} className="mr-1 inline" />
                        {t("common.delete")}
                    </DialogButton>
                    <div className="flex-1" />
                    <DialogButton onClick={onClose} disabled={busy}>
                        {t("common.cancel")}
                    </DialogButton>
                    <DialogButton
                        variant="primary"
                        onClick={handleRestore}
                        disabled={busy || loading}
                    >
                        {busy ? t("history.restore.restoring") : t("history.restore.action")}
                    </DialogButton>
                </>
            }
        >
            <div className="flex flex-col h-[480px]">
                {effectiveMeta && (
                    <div className="px-3 py-2 border-b border-border bg-muted/30 text-[11px] text-muted-foreground flex items-center gap-3 flex-wrap">
                        <span className="inline-flex items-center gap-1">
                            <Clock size={11} />
                            {formatTimestamp(effectiveMeta.createdAt)}
                        </span>
                        <span className="font-mono text-[10px] px-1.5 py-0.5 rounded bg-muted">
                            {t("explorer.history.wholeFile")}
                        </span>
                        <span className="font-mono text-[10px] px-1.5 py-0.5 rounded bg-muted">
                            {effectiveMeta.trigger}
                        </span>
                        {effectiveMeta.tag && <span className="text-amber-600">{effectiveMeta.tag}</span>}
                        {effectiveMeta.message && (
                            <span className="italic">"{effectiveMeta.message}"</span>
                        )}
                        <span className="ml-auto font-mono text-[10px] opacity-60">
                            {effectiveMeta.id.slice(0, 8)}
                        </span>
                    </div>
                )}
                <div className="flex border-b border-border bg-muted/10">
                    <TabButton active={tab === "content"} onClick={() => setTab("content")}>
                        <FileText size={12} />
                        {t("history.tab.content")}
                    </TabButton>
                    <TabButton active={tab === "diff"} onClick={() => setTab("diff")}>
                        <GitCompare size={12} />
                        {t("history.tab.diff")}
                    </TabButton>
                </div>
                <div className="flex-1 overflow-auto bg-background">
                    {error && (
                        <div className="p-3 text-[11px] text-red-500">{error}</div>
                    )}
                    {loading && !error && (
                        <div className="p-3 text-[11px] text-muted-foreground">{t("common.loading")}</div>
                    )}
                    {!loading && !error && tab === "content" && (
                        <pre className="m-0 p-3 text-[11px] font-mono whitespace-pre-wrap break-words leading-relaxed">
                            {snapshotContent ?? ""}
                        </pre>
                    )}
                    {!loading && !error && tab === "diff" && (
                        <DiffView text={diffText ?? ""} />
                    )}
                </div>
            </div>
        </DialogOverlay>
    );
}

function TabButton({
    active,
    onClick,
    children,
}: {
    active: boolean;
    onClick: () => void;
    children: ReactNode;
}) {
    return (
        <button
            type="button"
            onClick={onClick}
            className={cn(
                "px-3 py-1.5 text-[11px] inline-flex items-center gap-1 border-b-2 -mb-px",
                active
                    ? "border-foreground text-foreground"
                    : "border-transparent text-muted-foreground hover:text-foreground",
            )}
        >
            {children}
        </button>
    );
}

function DiffView({ text }: { text: string }) {
    const t = useT("editor");
    if (!text.trim()) {
        return (
            <div className="p-3 text-[11px] text-muted-foreground italic">
                {t("history.noDiff")}
            </div>
        );
    }
    return (
        <pre className="m-0 p-3 text-[11px] font-mono whitespace-pre leading-relaxed">
            {text.split("\n").map((line, i) => {
                let cls = "";
                if (line.startsWith("+") && !line.startsWith("+++")) cls = "text-emerald-600";
                else if (line.startsWith("-") && !line.startsWith("---")) cls = "text-red-500";
                else if (line.startsWith("@@")) cls = "text-blue-500";
                else if (line.startsWith("+++") || line.startsWith("---"))
                    cls = "text-muted-foreground";
                return (
                    <div key={i} className={cls}>
                        {line || "\u00A0"}
                    </div>
                );
            })}
        </pre>
    );
}

/* ── Undo Toast ── */

interface UndoToastRequest {
    message: string;
    onUndo: () => void | Promise<void>;
    durationMs: number;
}

let _toastListener: ((req: UndoToastRequest | null) => void) | null = null;

export interface ShowHistoryUndoToastOptions {
    message: string;
    onUndo: () => void | Promise<void>;
    /** Duration in ms (default 30000) */
    durationMs?: number;
}

export function showHistoryUndoToast(opts: ShowHistoryUndoToastOptions): void {
    if (!_toastListener) {
        console.warn("[history-toast] No HistoryUndoToastHost mounted");
        return;
    }
    _toastListener({
        message: opts.message,
        onUndo: opts.onUndo,
        durationMs: opts.durationMs ?? 30_000,
    });
}

export function HistoryUndoToastHost(): ReactNode {
    const t = useT("editor");
    const [toast, setToast] = useState<UndoToastRequest | null>(null);
    const [remaining, setRemaining] = useState(0);
    const intervalRef = useRef<number | null>(null);

    useEffect(() => {
        _toastListener = (req) => {
            setToast(req);
            setRemaining(req?.durationMs ?? 0);
        };
        return () => {
            _toastListener = null;
        };
    }, []);

    useEffect(() => {
        if (!toast) {
            if (intervalRef.current) {
                window.clearInterval(intervalRef.current);
                intervalRef.current = null;
            }
            return;
        }
        if (intervalRef.current) window.clearInterval(intervalRef.current);
        intervalRef.current = window.setInterval(() => {
            setRemaining((r) => {
                if (r <= 1000) {
                    setToast(null);
                    return 0;
                }
                return r - 1000;
            });
        }, 1000);
        return () => {
            if (intervalRef.current) {
                window.clearInterval(intervalRef.current);
                intervalRef.current = null;
            }
        };
    }, [toast]);

    if (!toast) return null;
    return createPortal(
        <div className="fixed bottom-6 left-1/2 -translate-x-1/2 z-[10000] flex items-center gap-3 px-4 py-2.5 rounded-lg shadow-lg border border-border bg-background text-foreground text-[12px] min-w-[320px]">
            <span className="flex-1">{toast.message}</span>
            <span className="text-muted-foreground text-[11px] font-mono">
                {Math.ceil(remaining / 1000)}s
            </span>
            <button
                type="button"
                onClick={async () => {
                    const request = toast;
                    setToast(null);
                    try {
                        const setStatusMessage = useEditorStore.getState().setStatusMessage;
                        setStatusMessage(t("history.undo.inProgress"), undefined);
                        await request.onUndo();
                        setStatusMessage(t("history.undo.success"), 4000);
                    } catch (err) {
                        const setStatusMessage = useEditorStore.getState().setStatusMessage;
                        console.warn("[history-toast] undo failed", err);
                        setStatusMessage(t("history.undo.failedWithMessage", {
                            message: err instanceof Error ? err.message : t("common.unknown"),
                        }), 4000);
                    }
                }}
                className="inline-flex items-center gap-1 px-2 py-1 rounded text-[11px] font-medium hover:bg-muted"
            >
                <Undo2 size={12} />
                {t("history.undo.action")}
            </button>
            <button
                type="button"
                onClick={() => setToast(null)}
                className="inline-flex items-center justify-center w-5 h-5 rounded hover:bg-muted text-muted-foreground"
            >
                <X size={12} />
            </button>
        </div>,
        document.body,
    );
}

/* ── Helpers ── */

function formatTimestamp(ts: number | string): string {
    const d = new Date(ts);
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")} ${String(d.getHours()).padStart(2, "0")}:${String(d.getMinutes()).padStart(2, "0")}:${String(d.getSeconds()).padStart(2, "0")}`;
}

function formatRelativeTime(
    ts: number | string,
    t: ReturnType<typeof useT>,
): string {
    const timestamp = typeof ts === "string" ? new Date(ts).getTime() : ts;
    const diffMs = Date.now() - timestamp;
    const sec = Math.round(diffMs / 1000);
    if (sec < 60) return t("history.relative.secondsAgo", { count: sec });
    const min = Math.round(sec / 60);
    if (min < 60) return t("history.relative.minutesAgo", { count: min });
    const hr = Math.round(min / 60);
    if (hr < 24) return t("history.relative.hoursAgo", { count: hr });
    return t("history.relative.daysAgo", { count: Math.round(hr / 24) });
}
